import {
  Box,
  Checkbox,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core';
import { fade, makeStyles, Theme } from '@material-ui/core/styles';
import {
  ClearRounded,
  DescriptionOutlined,
  VisibilityOutlined
} from '@material-ui/icons';
import { Autocomplete, Pagination } from '@material-ui/lab';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  getProjectDocumentVersionFilePreview,
  getProjectDocumentVersionFileWithWaterMark
} from '../../api/project-document-version-files';
import { DocumentMasterListItem } from '../../api/project-documents';
import { AppDispatch, RootState } from '../../app/store';
import { ORDER } from '../../common/enum/order';
import EnhancedTableToolbar from '../../components/EnhancedTableToolbar';
import getNotFoundText from '../../functions/getNotFoundText';
import { getCookie, updateCookie } from '../../functions/manageCookies';
import Breadcrumb from '../Breadcrumb';
import {
  AimsDocumentType,
  Discipline as DisciplineType,
  DocumentMasterListParams,
  getAimsDocumentTypesAsync,
  getDisciplinesAsync,
  getDocumentMasterListAsync,
  selectAimsDocumentTypes,
  selectDisciplines,
  selectDocuments
} from './documentMasterListSlice';


export default function DocumentMasterList() {
  const classes = useStyles();
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();

  const sortOptions = [
    { value: 'id', text: t('documentMasterList.sortOptions.createdDate') },
    { value: 'no', text: t('documentMasterList.sortOptions.documentCode') },
    { value: 'name', text: t('documentMasterList.sortOptions.documentName') }
  ];
  const { data: documentsData, count: documentsCount } = useSelector(selectDocuments);
  const { data: disciplinesData } = useSelector(selectDisciplines);
  const { data: aimsDocumentTypesData } = useSelector(selectAimsDocumentTypes);
  const { user } = useSelector((state: RootState) => state.userProfile);

  const [selectedDocuments, setSelectedDocuments] = useState<number[]>([]);

  // Pagination setup - matching StandardWorkflow pattern
  const pageKey = `[DOC]-userId${user?.id}-documentMasterList`;
  const cookieParams: DocumentMasterListParams | undefined = getCookie(pageKey);

  const defaultDocumentMasterListParams: DocumentMasterListParams = {
    page: 0,
    limit: 10,
    sortBy: 'name',
    orderBy: ORDER.asc,
    disciplineIds: [],
    aimsDocumentTypeIds: [],
    search: undefined
  };

  const [params, setParams] = useState<DocumentMasterListParams>({
    ...defaultDocumentMasterListParams,
    ...cookieParams
  });

  // Get filtered disciplines and document types based on selected IDs
  const filteredDisciplines = disciplinesData.filter((discipline: DisciplineType) =>
    params.disciplineIds?.some((disciplineId) => disciplineId === discipline.id)
  ) ?? [];

  const filteredDocumentTypes = aimsDocumentTypesData.filter((type: AimsDocumentType) =>
    params.aimsDocumentTypeIds?.some((typeId) => typeId === type.id)
  ) ?? [];

  // Main loading function - using new Document Master List API
  const loadingDocument = useCallback((params: DocumentMasterListParams) => {
    updateCookie(pageKey, params);
    dispatch(getDocumentMasterListAsync(params));
    setParams(params);
  }, [dispatch, pageKey]);

  // Load data on component mount
  useEffect(() => {
    loadingDocument(params);
    dispatch(getDisciplinesAsync());
    dispatch(getAimsDocumentTypesAsync());
  }, [dispatch, loadingDocument, params]);

  // Pagination handlers - matching StandardWorkflow pattern
  function handleChangePage<T>(
    newPage: number,
    params: T,
    loadingElement: (params: T) => void
  ) {
    loadingElement({
      ...params,
      page: newPage - 1 //The first value of pagination is 1, not 0
    });
  }

  function handleChangeLimit<T>(
    newLimit: number,
    params: T,
    loadingElement: (params: T) => void
  ) {
    loadingElement({
      ...params,
      page: 0,
      limit: newLimit
    });
  }

  function handleChangeDisciplines<T>(
    newDiscipline: DisciplineType[],
    params: T,
    loadingElement: (params: T) => void
  ) {
    const disciplineIds = newDiscipline.map((discipline) => discipline.id);
    loadingElement({
      ...params,
      page: 0,
      disciplineIds
    });
  }

  function handleChangeDocumentTypes<T>(
    newTypes: AimsDocumentType[],
    params: T,
    loadingElement: (params: T) => void
  ) {
    const aimsDocumentTypeIds = newTypes.map((type) => type.id);
    loadingElement({
      ...params,
      page: 0,
      aimsDocumentTypeIds
    });
  }

  // Helper function to check if file is PDF
  const isPdfFile = (document: DocumentMasterListItem) => {
    const revisionFiles = document.projectDocumentRevisions?.[0]?.projectDocumentRevisionFiles;
    return revisionFiles?.some(file =>
      file.s3ContentType?.toLowerCase().includes('pdf')
    ) || false;
  };

  // Helper function to get latest revision file
  const getLatestRevisionFile = (document: DocumentMasterListItem) => {
    return document.projectDocumentRevisions?.[0]?.projectDocumentRevisionFiles?.[0];
  };

  // Download file function
  const handleDownloadFile = async (document: DocumentMasterListItem) => {
    const file = getLatestRevisionFile(document);
    if (!file) {
      console.error('No file found for document:', document.id);
      return;
    }

    try {
      await getProjectDocumentVersionFileWithWaterMark({
        projectDocumentVersionFileId: file.id,
        contentType: file.s3ContentType,
        filename: `${document.no}_${document.name}`,
        positions: ['Center', 'Center'],
        rotations: [45, 45],
        colors: ['#EB4034', '#EB4034'],
        actionDate: new Date()
      });
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  };

  // Preview PDF function
  const handlePreviewFile = async (document: DocumentMasterListItem) => {
    const file = getLatestRevisionFile(document);
    if (!file) {
      console.error('No file found for document:', document.id);
      return;
    }

    try {
      await getProjectDocumentVersionFilePreview({
        projectDocumentVersionFileId: file.id,
        contentType: file.s3ContentType,
        filename: `${document.no}_${document.name}_preview`,
        actionDate: new Date()
      });
    } catch (error) {
      console.error('Error previewing file:', error);
    }
  };

  // Use aims document types from API

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedDocuments(documentsData.map((doc: DocumentMasterListItem) => doc.id));
    } else {
      setSelectedDocuments([]);
    }
  };

  const handleSelectDocument = (documentId: number) => {
    setSelectedDocuments(prev =>
      prev.includes(documentId)
        ? prev.filter(id => id !== documentId)
        : [...prev, documentId]
    );
  };

  // Use server-side pagination and filtering from API
  const totalCount = documentsCount;

  return (
    <div className={classes.root}>
      <Breadcrumb name="documentMasterList" />

      <div className={classes.title}>
        <Typography className={classes.flexGrow} component="p" variant="h4">
          {t('documentMasterList.title')}
        </Typography>
      </div>

      <div className={classes.description}>
        <Typography variant="body2" color="textSecondary">
          <Typography component="span" variant="subtitle2">
            {t('documentMasterList.subtitle')}
          </Typography>{' '}
          {t('documentMasterList.description')}
        </Typography>
      </div>

      <EnhancedTableToolbar<DocumentMasterListParams>
        name={t('documentMasterList.title') as string}
        params={params}
        sortOptions={sortOptions}
        loadingElement={loadingDocument}
        canSearchable
        searchHint={t('documentMasterList.searchHint') as string}
      />

      <Paper className={classes.filterBox}>
        <Autocomplete
          className={classes.filterInput}
          id="discipline"
          options={disciplinesData}
          multiple
          getOptionLabel={(option) => option?.name}
          renderTags={(tagValue, getTagProps) => {
            const maxTags = 6;
            const visibleTags = tagValue.slice(0, maxTags);
            const remainingCount = tagValue.length - maxTags;

            return (
              <>
                {visibleTags.map((option, index) => (
                  <Chip
                    key={option.id}
                    label={`${option?.name}`}
                    {...getTagProps({ index })}
                    variant="outlined"
                    size="small"
                    deleteIcon={<ClearRounded />}
                  />
                ))}
                {remainingCount > 0 && (
                  <Tooltip
                    title={
                      <div>
                        {tagValue.slice(maxTags).map((option, index) => (
                          <div key={option.id}>{option?.name}</div>
                        ))}
                      </div>
                    }
                    arrow
                  >
                    <Chip
                      label={`+${remainingCount} ${t('documentMasterList.actions.moreItems')}`}
                      variant="outlined"
                      size="small"
                      className={classes.moreChip}
                    />
                  </Tooltip>
                )}
              </>
            );
          }}
          value={filteredDisciplines}
          getOptionSelected={(option, value) => option.id === value.id}
          onChange={(event, newValue) => {
            handleChangeDisciplines(newValue, params, loadingDocument);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              size="small"
              placeholder={
                filteredDisciplines.length === 0 ? t('documentMasterList.filterByDiscipline') as string : ''
              }
            />
          )}
        />

        <Autocomplete
          className={classes.filterInput}
          id="document-type"
          options={aimsDocumentTypesData}
          multiple
          getOptionLabel={(option) => option?.name}
          renderTags={(tagValue, getTagProps) => {
            const maxTags = 2;
            const visibleTags = tagValue.slice(0, maxTags);
            const remainingCount = tagValue.length - maxTags;

            return (
              <>
                {visibleTags.map((option, index) => (
                  <Chip
                    key={option.id}
                    label={`${option?.name}`}
                    {...getTagProps({ index })}
                    variant="outlined"
                    size="small"
                    deleteIcon={<ClearRounded />}
                  />
                ))}
                {remainingCount > 0 && (
                  <Tooltip
                    title={
                      <div>
                        {tagValue.slice(maxTags).map((option, index) => (
                          <div key={option.id}>{option?.name}</div>
                        ))}
                      </div>
                    }
                    arrow
                  >
                    <Chip
                      label={`+${remainingCount} ${t('documentMasterList.actions.moreItems')}`}
                      variant="outlined"
                      size="small"
                      className={classes.moreChip}
                    />
                  </Tooltip>
                )}
              </>
            );
          }}
          value={filteredDocumentTypes}
          getOptionSelected={(option, value) => option.id === value.id}
          onChange={(event, newValue) => {
            handleChangeDocumentTypes(newValue, params, loadingDocument);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              size="small"
              placeholder={
                filteredDocumentTypes.length === 0 ? t('documentMasterList.filterByDocumentType') as string : ''
              }
            />
          )}
        />

        <FormControl
          className={classes.filterInput}
          variant="outlined"
          size="small"
        >
          <InputLabel id="limit-per-page-label">{t('documentMasterList.itemsPerPage')}</InputLabel>
          <Select
            labelId="limit-per-page-label"
            value={params.limit}
            onChange={(event) =>
              handleChangeLimit(
                event.target.value as number,
                params,
                loadingDocument
              )
            }
            label={t('documentMasterList.itemsPerPage')}
          >
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={25}>25</MenuItem>
            <MenuItem value={50}>50</MenuItem>
            <MenuItem value={100}>100</MenuItem>
          </Select>
        </FormControl>
      </Paper>

      {/* Document Table */}
      <TableContainer component={Paper} className={classes.table}>
        <Table stickyHeader size="small">
          <TableHead className={classes.stickyHeader}>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selectedDocuments.length > 0 && selectedDocuments.length < documentsData.length}
                  checked={documentsData.length > 0 && selectedDocuments.length === documentsData.length}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell>
                <Typography variant="subtitle2">
                  {t('documentMasterList.table.no')}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="subtitle2">
                  {t('documentMasterList.table.documentName')}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="subtitle2">
                  {t('documentMasterList.table.documentCode')}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="subtitle2">
                  {t('documentMasterList.table.publicDate')}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="subtitle2">
                  {t('documentMasterList.table.version')}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="subtitle2">
                  {t('documentMasterList.table.ownerDepartment')}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="subtitle2">
                  {t('documentMasterList.table.documentType')}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="subtitle2">
                  {t('documentMasterList.table.download')}
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {documentsData.length > 0 ? (
              documentsData.map((document: DocumentMasterListItem, index: number) => (
                <TableRow key={document.id} hover>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedDocuments.includes(document.id)}
                      onChange={() => handleSelectDocument(document.id)}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {(params.page || 0) * (params.limit || 10) + index + 1}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" className={classes.documentName}>
                      {document.name}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {document.document?.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={document.no}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {new Date(document.createdAt).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={document.projectDocumentRevisions?.[0]?.revision?.name || 'Rev 0'}
                      size="small"
                      className={classes.versionBadge}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {document.discipline?.name || t('documentMasterList.common.unknown')}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {document.discipline?.cfihosName || t('documentMasterList.common.unspecified')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {document.document.aimsDocumentType?.name || t('documentMasterList.common.unknown')}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {document.document.aimsDocumentType?.name || t('documentMasterList.common.unspecified')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" style={{ gap: 4 }}>
                      {/* Preview icon - only show for PDF files */}
                      {isPdfFile(document) && (
                        <Tooltip title={t('documentMasterList.actions.previewPdf') as string}>
                          <IconButton
                            size="small"
                            onClick={() => handlePreviewFile(document)}
                            disabled={!getLatestRevisionFile(document)}
                          >
                            <VisibilityOutlined fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}

                      {/* Download icon - always show */}
                      <Tooltip title={t('documentMasterList.actions.downloadFile') as string}>
                        <IconButton
                          size="small"
                          onClick={() => handleDownloadFile(document)}
                          disabled={!getLatestRevisionFile(document)}
                        >
                          <DescriptionOutlined fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  className={classes.fileNotFound}
                  colSpan={9}
                >
                  {getNotFoundText(params.search, t('common.document') as string, t('common.documents') as string, t)}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <div className={classes.pagination}>
        <Pagination
          count={Math.ceil(totalCount / (params.limit || 10))}
          shape="rounded"
          page={(params.page || 0) + 1}
          onChange={(event, newPage) => {
            handleChangePage<DocumentMasterListParams>(
              newPage,
              params,
              loadingDocument
            );
          }}
        />
      </div>
    </div>
  );
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    margin: theme.spacing(1.5, 0, 2, 0)
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    margin: theme.spacing(1.5, 0, 1, 0),
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      textAlign: 'center',
      margin: theme.spacing(3.5, 0, 2, 0)
    }
  },
  description: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: fade(theme.palette.primary.main, 0.04),
    borderRadius: theme.spacing(1),
    border: `1px solid ${fade(theme.palette.primary.main, 0.12)}`,
    [theme.breakpoints.down('xs')]: {
      marginBottom: theme.spacing(3),
      padding: theme.spacing(1.5)
    }
  },
  filterBox: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr auto',
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: '1fr',
      gridTemplateRows: 'repeat(3, 1fr)'
    },
    gap: theme.spacing(1),
    padding: theme.spacing(1),
    marginBottom: theme.spacing(1),
    borderRadius: theme.spacing(1)
  },
  filterInput: {
    minWidth: 144,
    '& .MuiInputBase-root': {
      height: 40,
      [theme.breakpoints.down('xs')]: {
        height: 'auto'
      }
    }
  },
  table: {
    marginBottom: theme.spacing(2),
    borderRadius: theme.spacing(2),
    maxHeight: window.innerHeight - 80
  },
  stickyHeader: {
    position: 'sticky',
    top: 0,
    background: theme.palette.background.default,
    zIndex: 10
  },
  documentName: {
    fontWeight: 500,
  },
  versionBadge: {
    backgroundColor: fade(theme.palette.primary.main, 0.1),
    color: theme.palette.primary.main,
    border: 'none',
    fontWeight: 500,
    fontSize: '0.8125rem',
    height: '26px',
    '& .MuiChip-label': {
      padding: '0 12px',
      fontWeight: 500,
    },
  },
  pagination: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(1.5)
  },
  flexGrow: {
    flexGrow: 1
  },
  moreChip: {
    backgroundColor: fade(theme.palette.primary.main, 0.1),
    color: theme.palette.primary.main,
    border: 'none',
    fontWeight: 500,
    fontSize: '0.8125rem',
    height: '26px',
    '& .MuiChip-label': {
      padding: '0 12px',
      fontWeight: 500,
    },
    '&:hover': {
      backgroundColor: fade(theme.palette.primary.main, 0.2),
    }
  },
  fileNotFound: {
    padding: theme.spacing(2, 1),
    textAlign: 'center'
  }
}));